import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField, Chip, FormControl, InputLabel } from '@mui/material';

// Helper function to flatten nested options
const flattenOptions = (options) => {
  return options.reduce((acc, option) => {
    acc.push(option?.label);
    if (option?.options && option?.options?.length > 0) {
      acc.push(...flattenOptions(option?.options));
    }
    return acc;
  }, []);
};

const MultiSelectDropdown = ({ data, name, value, onChange, error }) => {
  // Flatten the options so both parent and nested options are available
  const flatOptions = flattenOptions(data.options);

  const handleAutocompleteChange = (event, newValue) => {
    let finalValue = newValue;

    // Convert array to comma-separated string if multiple selection is allowed
    if (data.allowMultipleSelection && Array.isArray(newValue)) {
      finalValue = newValue.join(', ');
    }

    // Call the parent's onChange handler
    onChange({
      target: {
        name,
        value: finalValue,
      },
    });
  };

  return (
    <FormControl fullWidth margin="normal" variant="standard" error={error}>
      <div className="mb-3">
        <InputLabel shrink>{data.label}</InputLabel>
      </div>
      <Autocomplete
        multiple={data.allowMultipleSelection}
        freeSolo={data.allowFreeField}
        options={flatOptions}
        value={data.allowMultipleSelection && value ? value.split(', ') || '' : value || []}
        onChange={handleAutocompleteChange}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => <Chip key={index} label={option} {...getTagProps({ index })} />)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            required={data.required}
            error={error}
            helperText={error ? 'Please select a valid value' : ''}
          />
        )}
      />
    </FormControl>
  );
};

MultiSelectDropdown.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
    required: PropTypes.bool,
    allowFreeField: PropTypes.bool,
    allowMultipleSelection: PropTypes.bool,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        required: PropTypes.bool,
        options: PropTypes.array, // Nested options
      })
    ).isRequired,
  }).isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export default MultiSelectDropdown;
